import { differenceInCalendarDays, differenceInDays, endOfDay } from 'date-fns';

import type { ValueListProps } from '@components/value-list';
import type { CareerSiteLanguageType } from '@locale/locale-store';
import type { Color } from '@styles/theme';
import { abbreviateDate, parseDate } from '@utils/date';
import { getTranslatedTitle } from '@utils/translated-title';

import { isJobExpired, jobState } from 'helpers/job-lifecycle';
import type { Translate } from 'helpers/job-publish';
import type { JobListRow } from 'types/jobs';

import { COLUMN_BREAKPOINTS, DEADLINE_WARNING_DAYS, jobsLocale } from './jobs-config';

export type { Translate };

type UiLanguage = 'is' | 'en';

type ValueListItem = ValueListProps['items'][number];

export const jobDetailUrl = (job: JobListRow) => `/jobs/${job.id}`;

const jobDaysOpen = (job: JobListRow) =>
  job.datePublished ? differenceInCalendarDays(new Date(), parseDate(job.datePublished)) : 0;

const jobDaysUntilDeadline = (job: JobListRow) =>
  job.deadline ? differenceInDays(endOfDay(parseDate(job.deadline)), new Date()) : 0;

type LanguageInput = {
  job: JobListRow;
  lang: UiLanguage;
  defaultLanguage: CareerSiteLanguageType | undefined;
};

export const jobLocationDisplay = ({
  job,
  lang,
  defaultLanguage,
  t,
}: LanguageInput & { t: Translate }) =>
  getTranslatedTitle(job.location?.translations, lang, defaultLanguage) ?? t('job.nolocation');

export const jobWorkTypeDisplay = ({ job, lang, defaultLanguage }: LanguageInput) =>
  getTranslatedTitle(job.workType?.translations, lang, defaultLanguage) ?? '';

export const jobTitle = ({
  job,
  defaultLanguage,
  t,
}: {
  job: JobListRow;
  defaultLanguage: CareerSiteLanguageType | undefined;
  t: Translate;
}) => {
  const languages = job.languages ?? [];
  const preferred = languages.find((language) => language.language === defaultLanguage);
  const chosen =
    preferred && preferred.isEnabled
      ? preferred
      : (languages.find((language) => language.isEnabled) ?? languages[0]);

  return chosen?.title || t('job.notitle');
};

type DisplayInput = { job: JobListRow; lang: UiLanguage; t: Translate };

const dateOrFallback = ({
  value,
  key,
  lang,
  t,
}: {
  value: string | null;
  key: string;
  lang: UiLanguage;
  t: Translate;
}) => (value ? t(`job.${key}`, abbreviateDate(value, lang)) : t(`job.no${key}`));

export const jobMetaLine = ({
  job,
  lang,
  defaultLanguage,
  t,
}: LanguageInput & { t: Translate }) => {
  const parts = [
    jobWorkTypeDisplay({ job, lang, defaultLanguage }),
    jobLocationDisplay({ job, lang, defaultLanguage, t }),
  ];

  if (!isJobExpired(job) && !job.isArchived) {
    parts.push(job.deadline ? abbreviateDate(job.deadline, lang) : t('job.nodeadline'));
  }

  return parts.filter(Boolean).join(' • ');
};

export const jobDateText = ({ job, lang, t }: DisplayInput) => {
  const expired = isJobExpired(job);

  if (job.isPublished && !job.isArchived && !expired) {
    if (job.datePublished) return t('job.datepublished', abbreviateDate(job.datePublished, lang));
    return t('job.unknowndatepublished');
  }
  if (!job.isPublished && !job.isArchived) {
    if (job.dateScheduledPublish && jobState(job) === 'draft') {
      return t('job.datescheduledpublish', abbreviateDate(job.dateScheduledPublish, lang));
    }
    return dateOrFallback({ value: job.dateCreated, key: 'datecreated', lang, t });
  }
  if (job.isPublished && !job.isArchived && expired) {
    return dateOrFallback({ value: job.deadline, key: 'dateexpired', lang, t });
  }
  return dateOrFallback({ value: job.dateArchived, key: 'datearchived', lang, t });
};

export const newApplicantsValueColor = (count: number): Color | undefined =>
  count > 0 ? 'elementGreen100' : undefined;

/** Progress columns for a published job, outcome columns otherwise. */
export const jobListValues = ({
  job,
  width,
  t,
}: {
  job: JobListRow;
  width: number;
  t: (key: keyof typeof jobsLocale.en) => string;
}): ValueListItem[] => {
  const counts = jobCounts(job);
  const isAcceptingApplicants = jobState(job) === 'published';
  const daysLeft = jobDaysUntilDeadline(job);

  const columns: (ValueListItem & { isShown: boolean })[] = [
    {
      label: t('count_new'),
      value: counts.new,
      valueColor: newApplicantsValueColor(counts.new),
      isShown: isAcceptingApplicants,
    },
    { label: t('count_applicants'), value: counts.total, isShown: true },
    {
      label: t('count_active'),
      value: counts.active,
      labelColor: 'elementBlue100',
      isShown: !isAcceptingApplicants,
    },
    {
      label: t('count_retracted'),
      value: counts.retracted,
      labelColor: 'elementOrange100',
      isShown: !isAcceptingApplicants && width > COLUMN_BREAKPOINTS.retracted,
    },
    {
      label: t('count_rejected'),
      value: counts.rejected,
      labelColor: 'elementRed100',
      isShown: !isAcceptingApplicants && width > COLUMN_BREAKPOINTS.rejected,
    },
    {
      label: t('count_hired'),
      value: counts.hired,
      labelColor: 'elementGreen200',
      isShown: !isAcceptingApplicants && width > COLUMN_BREAKPOINTS.hired,
    },
    {
      label: t('count_daysOpen'),
      value: jobDaysOpen(job),
      isShown: isAcceptingApplicants && width > COLUMN_BREAKPOINTS.daysOpen,
    },
    {
      label: t('count_daysLeft'),
      value: daysLeft,
      valueColor: daysLeft < DEADLINE_WARNING_DAYS ? 'elementOrange100' : undefined,
      // Hidden without a deadline — a placeholder glyph in a numeric column reads as a glitch.
      isShown:
        isAcceptingApplicants && Boolean(job.deadline) && width > COLUMN_BREAKPOINTS.daysLeft,
    },
    {
      label: t('count_access'),
      // The Access tab excludes inactive and system users; documents indexed before the flags lack them.
      value: (job.followers ?? []).filter(
        (follower) => follower.isActive !== false && follower.isSystemUser !== true,
      ).length,
      isShown: width > COLUMN_BREAKPOINTS.access,
    },
  ];

  return columns.filter((column) => column.isShown).map(({ isShown, ...item }) => item);
};

export const jobCounts = (job: JobListRow) => {
  const total = job.counts?.total ?? 0;
  const hired = job.counts?.hired ?? 0;
  const rejected = job.counts?.rejected ?? 0;
  const retracted = job.counts?.retracted ?? 0;

  return {
    total,
    hired,
    rejected,
    retracted,
    new: job.counts?.new ?? 0,
    active: Math.max(0, total - rejected - hired - retracted),
  };
};
